import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import * as XLSX from "xlsx";
import { getAuthHeaders } from "../../config/axios";
import "./Tenderperiodreport.css";

/* ══════════════════════════════════════════════════════
   CONFIG — change these two lines if your backend differs
   REPORT_API_PATH : the endpoint that returns the JSON you shared
   PERIOD_PARAM    : query param used to pick last_week / this_week …
══════════════════════════════════════════════════════ */
const BASE = "https://bd.aimantra.info";
// const BASE = "https://bkzrdhbv-8008.inc1.devtunnels.ms";
const REPORT_API_PATH = "/siteproject/new-tender-cohort-report/";
const PERIOD_PARAM = "period";

const PERIODS = [
    { key: "last_week", label: "Last week" },
    { key: "this_week", label: "This week" },
    { key: "last_month", label: "Last month" },
    { key: "this_month", label: "This month" },
    { key: "custom", label: "Custom range" },
];

/* ── status / stage metadata ─────────────────────────── */
const STATUS_ORDER = [
    "new_pending", "finalized_pending", "in_bid_prep", "submitted",
    "technical_open", "financial_open", "expired", "closed_by_us", "cancelled_by_client",
];
const STATUS_TONE = {
    new_pending: "blue", finalized_pending: "indigo", in_bid_prep: "cyan",
    submitted: "green", technical_open: "teal", financial_open: "purple",
    expired: "red", closed_by_us: "amber", cancelled_by_client: "grey",
};
const STATUS_FALLBACK_LABEL = {
    new_pending: "Still in New", finalized_pending: "In Final, not in Bid Prep",
    in_bid_prep: "Lying in Bid Prep", submitted: "Submitted",
    technical_open: "Technical open", financial_open: "Financial open",
    expired: "Expired", closed_by_us: "Closed by us", cancelled_by_client: "Cancelled by client",
};
const STAGE_LIST_KEY = {
    new: "all", finalized: "reached_finalized", bid_prep: "reached_bid_prep",
    submitted: "reached_submitted", technical_open: "reached_technical_open",
    financial_open: "reached_financial_open",
};
const STAGE_TABS = [
    { key: "reached_finalized", label: "Reached Final" },
    { key: "reached_bid_prep", label: "Reached Bid Prep" },
    { key: "reached_submitted", label: "Reached Submitted" },
    { key: "reached_technical_open", label: "Reached Tech Open" },
    { key: "reached_financial_open", label: "Reached Financial Open" },
];
const STAGE_TONE = { new: "blue", finalized: "indigo", bid_prep: "cyan", submitted: "green", technical_open: "teal", financial_open: "purple" };

const DIMENSIONS = [
    { key: "client", label: "Client" },
    { key: "sector", label: "Sector" },
    { key: "state", label: "State" },
    { key: "tender_type", label: "Tender type" },
    { key: "lead_partner", label: "Lead partner" },
];

/* ── helpers ─────────────────────────────────────────── */
const pretty = (s) => (s ? String(s).replace(/_/g, " ") : "—");
const nf = (v) => (v == null ? "—" : Number(v).toLocaleString("en-IN"));
const pctTxt = (v) => (v == null ? "—" : `${Number(v).toFixed(1).replace(/\.0$/, "")}%`);
const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtDateTime = (iso) =>
    iso
        ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true })
        : "—";
const daysLeft = (iso) => (iso ? Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000) : null);
const dayTone = (d) => (d == null ? "grey" : d < 0 ? "grey" : d <= 3 ? "red" : d <= 7 ? "amber" : "green");
const dayText = (d) => (d == null ? "—" : d < 0 ? `${Math.abs(d)}d ago` : d === 0 ? "Today" : `${d}d left`);
const yesNo = (v) => (v === true ? "Yes" : v === false ? "No" : "—");
const entryLabel = (e) => e?.label || e?.stage_label || e?.stage || e?.reason || e?.key || e?.name || "—";
const entryCount = (e) => e?.count ?? e?.total ?? e?.tender_count ?? 0;

/* ── data hook ───────────────────────────────────────── */
function useReport(period, dateFrom, dateTo, reloadKey) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (period === "custom" && (!dateFrom || !dateTo)) return undefined;
        const ctrl = new AbortController();
        setLoading(true);
        setError(null);
        const qs = new URLSearchParams();
        if (period === "custom") {
            qs.set("date_from", dateFrom);
            qs.set("date_to", dateTo);
        } else {
            qs.set(PERIOD_PARAM, period);
        }
        fetch(`${BASE}${REPORT_API_PATH}?${qs}`, {
            signal: ctrl.signal,
            headers: getAuthHeaders(),
        })
            .then(async (r) => {
                const j = await r.json().catch(() => null);
                if (!r.ok) throw new Error(j?.error || j?.detail || `Server returned ${r.status}`);
                if (j && j.success === false) throw new Error(j.error || "The report could not be generated");
                return j;
            })
            .then((j) => { setData(j); setLoading(false); })
            .catch((e) => {
                if (e.name === "AbortError") return;
                setError(e.message || "Request failed");
                setLoading(false);
            });
        return () => ctrl.abort();
    }, [period, dateFrom, dateTo, reloadKey]);

    return { data, loading, error };
}

/* ══════════════════════════════════════════════════════
   SMALL PIECES
══════════════════════════════════════════════════════ */
function Pill({ tone = "grey", children, title }) {
    return <span className={`tpr-pill tpr-pill--${tone}`} title={title}>{children}</span>;
}

function SectionHead({ title, hint, right }) {
    return (
        <div className="tpr-sec-head">
            <div>
                <h3 className="tpr-sec-title">{title}</h3>
                {hint && <p className="tpr-sec-hint">{hint}</p>}
            </div>
            {right}
        </div>
    );
}

function KpiCard({ label, value, note, tone, onClick, active }) {
    return (
        <button type="button" className={`tpr-kpi tpr-kpi--${tone}${active ? " tpr-kpi--active" : ""}`} onClick={onClick}>
            <span className="tpr-kpi-value">{nf(value)}</span>
            <span className="tpr-kpi-label">{label}</span>
            {note && <span className="tpr-kpi-note">{note}</span>}
        </button>
    );
}

/* funnel ─ each stage is a bar sized by % of new tenders */
function Funnel({ stages, onPick, activeKey }) {
    if (!stages?.length) return <p className="tpr-empty">No funnel data for this period.</p>;
    return (
        <ol className="tpr-funnel">
            {stages.map((s, i) => {
                const w = Math.max(Number(s.pct_of_new) || 0, s.count > 0 ? 3 : 0);
                const tone = STAGE_TONE[s.stage] || "blue";
                const prev = i > 0 ? stages[i - 1] : null;
                const lost = prev ? Math.max(prev.count - s.count, 0) : 0;
                return (
                    <li key={s.stage} className="tpr-funnel-row">
                        <button
                            type="button"
                            className={`tpr-funnel-btn${activeKey === s.stage ? " tpr-funnel-btn--active" : ""}`}
                            onClick={() => onPick(s.stage)}
                            title="Show these tenders in the table"
                        >
                            <span className="tpr-funnel-label">{s.label}</span>
                            <span className="tpr-funnel-track">
                                <span className={`tpr-funnel-fill tpr-fill--${tone}`} style={{ "--w": `${w}%` }} />
                            </span>
                            <span className="tpr-funnel-count">{nf(s.count)}</span>
                            <span className="tpr-funnel-pct">{pctTxt(s.pct_of_new)} of new</span>
                        </button>
                        {prev && (
                            <div className="tpr-funnel-step">
                                <span>{s.pct_of_previous_stage == null ? "—" : `${pctTxt(s.pct_of_previous_stage)} of previous stage`}</span>
                                {lost > 0 && <span className="tpr-funnel-lost">{nf(lost)} did not move on</span>}
                            </div>
                        )}
                    </li>
                );
            })}
        </ol>
    );
}

/* current status ─ stacked bar + legend list */
function StatusBreakdown({ current, onPick, activeKey }) {
    const rows = STATUS_ORDER.map((k) => ({
        key: k,
        label: current?.[k]?.label || STATUS_FALLBACK_LABEL[k],
        count: current?.[k]?.count ?? 0,
        pct: current?.[k]?.pct_of_new ?? 0,
        tone: STATUS_TONE[k],
    }));
    const total = rows.reduce((a, r) => a + r.count, 0);
    return (
        <div>
            <div className="tpr-stack" role="img" aria-label="Where this period's tenders are now">
                {rows.filter((r) => r.count > 0).map((r) => (
                    <span key={r.key} className={`tpr-stack-seg tpr-fill--${r.tone}`} style={{ flexGrow: r.count }} title={`${r.label}: ${r.count}`} />
                ))}
                {total === 0 && <span className="tpr-stack-empty" />}
            </div>
            <ul className="tpr-status-list">
                {rows.map((r) => (
                    <li key={r.key}>
                        <button
                            type="button"
                            className={`tpr-status-item${activeKey === r.key ? " tpr-status-item--active" : ""}${r.count === 0 ? " tpr-status-item--zero" : ""}`}
                            onClick={() => onPick(r.key)}
                        >
                            <span className={`tpr-dot tpr-fill--${r.tone}`} />
                            <span className="tpr-status-name">{r.label}</span>
                            <span className="tpr-status-count">{nf(r.count)}</span>
                            <span className="tpr-status-pct">{pctTxt(r.pct)}</span>
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/* closure cards (expired / closed by us / cancelled) */
function ClosureCard({ title, tone, block, extra, onOpen }) {
    const total = block?.total ?? 0;
    const byStage = block?.by_stage || [];
    const reasons = block?.reasons || [];
    const maxStage = Math.max(...byStage.map(entryCount), 1);
    const maxReason = Math.max(...reasons.map(entryCount), 1);
    return (
        <article className={`tpr-closure tpr-closure--${tone}`}>
            <header className="tpr-closure-head">
                <div>
                    <h4>{title}</h4>
                    <span className="tpr-closure-total">{nf(total)}</span>
                </div>
                {total > 0 && <button type="button" className="tpr-link-btn" onClick={onOpen}>View tenders</button>}
            </header>
            {total === 0 ? (
                <p className="tpr-closure-none">None in this period.</p>
            ) : (
                <>
                    {extra}
                    {byStage.length > 0 && (
                        <div className="tpr-mini-block">
                            <h5>Stage when it ended</h5>
                            {byStage.map((e, i) => (
                                <div key={i} className="tpr-mini-row">
                                    <span className="tpr-mini-name">{pretty(entryLabel(e))}</span>
                                    <span className="tpr-mini-track"><span className={`tpr-fill--${tone}`} style={{ "--w": `${(entryCount(e) / maxStage) * 100}%` }} /></span>
                                    <span className="tpr-mini-count">{entryCount(e)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    {reasons.length > 0 && (
                        <div className="tpr-mini-block">
                            <h5>Reasons</h5>
                            {reasons.map((e, i) => (
                                <div key={i} className="tpr-mini-row">
                                    <span className="tpr-mini-name">{pretty(entryLabel(e))}</span>
                                    <span className="tpr-mini-track"><span className={`tpr-fill--${tone}`} style={{ "--w": `${(entryCount(e) / maxReason) * 100}%` }} /></span>
                                    <span className="tpr-mini-count">{entryCount(e)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </article>
    );
}

/* breakdown bars with dimension chips; clicking a bar filters the table */
function Breakdown({ list, filters, onPick }) {
    const [dim, setDim] = useState("client");
    const rows = useMemo(() => {
        const m = {};
        list.forEach((t) => {
            const k = t[dim] || "Unspecified";
            m[k] = (m[k] || 0) + 1;
        });
        return Object.entries(m).map(([key, count]) => ({ key, count })).sort((a, b) => b.count - a.count);
    }, [list, dim]);
    const max = Math.max(...rows.map((r) => r.count), 1);
    const [all, setAll] = useState(false);
    const shown = all ? rows : rows.slice(0, 8);
    return (
        <div>
            <div className="tpr-chips" role="tablist">
                {DIMENSIONS.map((d) => (
                    <button key={d.key} type="button" role="tab" aria-selected={dim === d.key}
                        className={`tpr-chip${dim === d.key ? " tpr-chip--on" : ""}`} onClick={() => { setDim(d.key); setAll(false); }}>
                        {d.label}
                    </button>
                ))}
            </div>
            {rows.length === 0 ? <p className="tpr-empty">Nothing to break down.</p> : (
                <ul className="tpr-bars">
                    {shown.map((r) => {
                        const on = filters[dim] === r.key;
                        return (
                            <li key={r.key}>
                                <button type="button" className={`tpr-bar${on ? " tpr-bar--on" : ""}`} onClick={() => onPick(dim, on ? "" : r.key)} title={`Filter table by ${r.key}`}>
                                    <span className="tpr-bar-name">{pretty(r.key)}</span>
                                    <span className="tpr-bar-track"><span style={{ "--w": `${(r.count / max) * 100}%` }} /></span>
                                    <span className="tpr-bar-count">{r.count}</span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
            {rows.length > 8 && (
                <button type="button" className="tpr-link-btn tpr-more" onClick={() => setAll((v) => !v)}>
                    {all ? "Show fewer" : `Show ${rows.length - 8} more`}
                </button>
            )}
        </div>
    );
}

/* next deadlines among tenders that are still active */
function Deadlines({ list, onOpen }) {
    const rows = useMemo(
        () => list
            .filter((t) => ["new_pending", "finalized_pending", "in_bid_prep"].includes(t.current_status) && t.submission_date && new Date(t.submission_date) > new Date())
            .sort((a, b) => new Date(a.submission_date) - new Date(b.submission_date))
            .slice(0, 6),
        [list]
    );
    if (!rows.length) return <p className="tpr-empty">No upcoming deadlines among active tenders.</p>;
    return (
        <ul className="tpr-deadlines">
            {rows.map((t) => {
                const d = daysLeft(t.submission_date);
                return (
                    <li key={t.id}>
                        <button type="button" className="tpr-deadline" onClick={() => onOpen(t.id)}>
                            <span className={`tpr-deadline-days tpr-pill--${dayTone(d)}`}>{dayText(d)}</span>
                            <span className="tpr-deadline-main">
                                <span className="tpr-deadline-name">{t.tender_name}</span>
                                <span className="tpr-deadline-meta">{pretty(t.client)} · {fmtDate(t.submission_date)} · {t.status_label}</span>
                            </span>
                        </button>
                    </li>
                );
            })}
        </ul>
    );
}

/* expanded row — every remaining field */
function DetailPanel({ t }) {
    const groups = [
        {
            title: "Tender",
            rows: [
                ["Record ID", t.id], ["Tender ID", t.tender_id], ["Project code (BD)", t.company_project_code_bd],
                ["Client", pretty(t.client)], ["Sector", t.sector], ["State", t.state], ["Tender type", pretty(t.tender_type)],
                ["Lead partner", t.lead_partner || "—"], ["Cost of consultancy", t.cost_of_consult ?? "—"],
            ],
        },
        {
            title: "Dates",
            rows: [
                ["Added on", fmtDateTime(t.created_at)], ["Submission date", fmtDateTime(t.submission_date)],
                ["Corrigendum date", fmtDateTime(t.corrigendum_date)], ["Finalized on (date_cc)", fmtDateTime(t.date_cc)],
                ["Date EE", fmtDateTime(t.date_ee)], ["Bid prep started (date_ff)", fmtDateTime(t.date_ff)],
            ],
        },
        {
            title: "Progress flags",
            rows: [
                ["Finalized", yesNo(t.is_finalize)], ["Bid prep in progress", yesNo(t.is_bid_prepare_in_progress)],
                ["Submitted", yesNo(t.is_submitted)], ["In technical tab", yesNo(t.is_in_tech_tab)],
                ["In financial tab", yesNo(t.is_in_financial_tab)], ["Stage reached", t.stage_reached_label || pretty(t.stage_reached)],
                ["Current status", t.status_label || pretty(t.current_status)],
            ],
        },
        {
            title: "Closure",
            rows: [
                ["Closed by us", yesNo(t.is_rejected)], ["Rejected reason", pretty(t.rejected_reason)],
                ["Other reason", t.rejected_reason_other || "—"], ["Cancelled by client", yesNo(t.is_cancelled)],
                ["Cancellation remark", t.reason_remark_cancellation || "—"], ["Cancellation proof uploaded", yesNo(t.cancellation_proof)],
                ["Closure reason", t.closure_reason || "—"],
            ],
        },
    ];
    return (
        <div className="tpr-detail">
            <p className="tpr-detail-name">{t.tender_name}</p>
            <div className="tpr-detail-grid">
                {groups.map((g) => (
                    <section key={g.title} className="tpr-detail-group">
                        <h5>{g.title}</h5>
                        <dl>
                            {g.rows.map(([k, v]) => (
                                <div key={k} className="tpr-detail-row">
                                    <dt>{k}</dt>
                                    <dd>{v === "" || v == null ? "—" : String(v)}</dd>
                                </div>
                            ))}
                        </dl>
                    </section>
                ))}
            </div>
        </div>
    );
}

/* ══════════════════════════════════════════════════════
   MAIN
══════════════════════════════════════════════════════ */
const PAGE_SIZES = [10, 15, 25, 50];
const SORTS = {
    created_at: (t) => new Date(t.created_at || 0).getTime(),
    submission_date: (t) => new Date(t.submission_date || 0).getTime(),
    tender_id: (t) => String(t.tender_id || ""),
    client: (t) => String(t.client || ""),
    sector: (t) => String(t.sector || ""),
    state: (t) => String(t.state || ""),
    stage: (t) => String(t.stage_reached || ""),
    status: (t) => String(t.current_status || ""),
    code: (t) => String(t.company_project_code_bd || ""),
};

export default function TenderPeriodReport() {
    const [period, setPeriod] = useState("last_week");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [reloadKey, setReloadKey] = useState(0);
    const { data, loading, error } = useReport(period, dateFrom, dateTo, reloadKey);

    const [view, setView] = useState("status"); // status | stage
    const [tab, setTab] = useState("all");
    const [q, setQ] = useState("");
    const [filters, setFilters] = useState({ sector: "", client: "", state: "", tender_type: "", lead_partner: "" });
    const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const [open, setOpen] = useState(null);
    const tableRef = useRef(null);

    const T = data?.tenders || {};
    const allTenders = T.all_tenders || [];

    const jump = useCallback((v, t) => {
        setView(v);
        setTab(t);
        setPage(1);
        setOpen(null);
        requestAnimationFrame(() => tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
    }, []);

    const baseList = useMemo(() => {
        if (tab === "all") return allTenders;
        return (view === "status" ? T.by_status?.[tab] : T.by_stage?.[tab]) || [];
    }, [T, allTenders, view, tab]);

    const afterDropdowns = useMemo(
        () => baseList.filter((t) => Object.entries(filters).every(([k, v]) => !v || (t[k] || "Unspecified") === v)),
        [baseList, filters]
    );

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return afterDropdowns;
        return afterDropdowns.filter((t) =>
            [t.tender_id, t.company_project_code_bd, t.tender_name, t.client, t.state, t.sector, t.tender_type, t.lead_partner, t.status_label, t.stage_reached_label]
                .some((v) => String(v || "").toLowerCase().includes(s)));
    }, [afterDropdowns, q]);

    const sorted = useMemo(() => {
        const fn = SORTS[sort.key] || SORTS.created_at;
        const out = [...filtered].sort((a, b) => {
            const x = fn(a), y = fn(b);
            return x < y ? -1 : x > y ? 1 : 0;
        });
        return sort.dir === "desc" ? out.reverse() : out;
    }, [filtered, sort]);

    const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
    const safePage = Math.min(page, pages);
    const rows = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);

    const options = useMemo(() => {
        const uniq = (k) => [...new Set(allTenders.map((t) => t[k] || "Unspecified"))].sort();
        return { sector: uniq("sector"), client: uniq("client"), state: uniq("state"), tender_type: uniq("tender_type") };
    }, [allTenders]);

    const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };
    const activeFilterCount = Object.values(filters).filter(Boolean).length + (q ? 1 : 0);
    const clearAll = () => { setFilters({ sector: "", client: "", state: "", tender_type: "", lead_partner: "" }); setQ(""); setPage(1); };
    const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
    const sortMark = (key) => (sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : "");

    const openFromDeadline = (id) => {
        jump("status", "all");
        setQ("");
        const idx = [...allTenders].sort((a, b) => SORTS.created_at(b) - SORTS.created_at(a)).findIndex((t) => t.id === id);
        if (idx >= 0) { setSort({ key: "created_at", dir: "desc" }); setPage(Math.floor(idx / pageSize) + 1); setOpen(id); }
    };

    function exportXlsx() {
        const out = sorted.map((t) => ({
            "Project Code": t.company_project_code_bd, "Tender ID": t.tender_id, "Tender Name": t.tender_name,
            Client: pretty(t.client), Sector: t.sector, State: t.state, "Tender Type": pretty(t.tender_type),
            "Added On": fmtDateTime(t.created_at), "Submission Date": fmtDateTime(t.submission_date),
            "Corrigendum Date": fmtDateTime(t.corrigendum_date), "Finalized On": fmtDateTime(t.date_cc),
            "Date EE": fmtDateTime(t.date_ee), "Bid Prep Started": fmtDateTime(t.date_ff),
            "Lead Partner": t.lead_partner || "", "Cost of Consultancy": t.cost_of_consult ?? "",
            Finalized: yesNo(t.is_finalize), "Bid Prep In Progress": yesNo(t.is_bid_prepare_in_progress),
            Submitted: yesNo(t.is_submitted), "In Tech Tab": yesNo(t.is_in_tech_tab), "In Financial Tab": yesNo(t.is_in_financial_tab),
            "Closed By Us": yesNo(t.is_rejected), "Rejected Reason": pretty(t.rejected_reason),
            "Rejected Reason (Other)": t.rejected_reason_other || "", "Cancelled By Client": yesNo(t.is_cancelled),
            "Cancellation Remark": t.reason_remark_cancellation || "", "Cancellation Proof": yesNo(t.cancellation_proof),
            "Stage Reached": t.stage_reached_label, "Current Status": t.status_label, "Closure Reason": t.closure_reason || "",
        }));
        const wb = XLSX.utils.book_new();
        const summary = data?.summary || {};
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(Object.entries(summary).map(([k, v]) => ({ Metric: pretty(k), Value: v }))), "Summary");
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(out), "Tenders");
        XLSX.writeFile(wb, `tender_report_${data?.period?.date_from || "period"}_to_${data?.period?.date_to || ""}.xlsx`);
    }

    const S = data?.summary || {};
    const P = data?.period || {};
    const funnel = data?.funnel || [];
    const newCount = S.new_tenders_added ?? 0;
    const activeStageKey = view === "stage" ? Object.keys(STAGE_LIST_KEY).find((k) => STAGE_LIST_KEY[k] === tab) : tab === "all" ? "new" : null;

    /* headline sentences */
    const insights = useMemo(() => {
        if (!data) return [];
        const out = [];
        const fin = funnel.find((s) => s.stage === "finalized");
        if (newCount > 0 && fin) out.push(`${pctTxt(fin.pct_of_new)} of the ${nf(newCount)} new tenders were finalized (${nf(fin.count)}).`);
        const pending = data.current_status?.new_pending?.count ?? 0;
        if (pending > 0) out.push(`${nf(pending)} tenders are still in New and waiting for a decision.`);
        if ((S.currently_lying_in_bid_prep ?? 0) > 0 && (S.submitted ?? 0) === 0) out.push(`${nf(S.currently_lying_in_bid_prep)} tenders are in Bid Prep and none has been submitted yet.`);
        const lost = (S.expired ?? 0) + (S.closed_by_us ?? 0) + (S.cancelled_by_client ?? 0);
        out.push(lost === 0 ? "No tenders expired, were closed or were cancelled in this period." : `${nf(lost)} tenders ended without a submission (expired, closed or cancelled).`);
        return out;
    }, [data, funnel, newCount, S]);

    /* ── render ── */
    return (
        <div className="tpr-root">
            {/* toolbar */}
            <header className="tpr-toolbar">
                <div className="tpr-title-block">
                    <h2 className="tpr-title">Tender period report</h2>
                    <p className="tpr-subtitle">
                        {P.date_from ? <>Tenders added between <strong>{fmtDate(P.date_from)}</strong> and <strong>{fmtDate(P.date_to)}</strong></> : "Choose a period to load the report"}
                    </p>
                </div>
                <div className="tpr-toolbar-actions">
                    <div className="tpr-seg" role="tablist" aria-label="Report period">
                        {PERIODS.map((p) => (
                            <button key={p.key} type="button" role="tab" aria-selected={period === p.key}
                                className={`tpr-seg-btn${period === p.key ? " tpr-seg-btn--on" : ""}`}
                                onClick={() => { setPeriod(p.key); setPage(1); }}>
                                {p.label}
                            </button>
                        ))}
                    </div>
                    {period === "custom" && (
                        <div className="tpr-range">
                            <label>From <input type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} /></label>
                            <label>To <input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} /></label>
                        </div>
                    )}
                    <button type="button" className="tpr-btn tpr-btn--ghost" onClick={() => setReloadKey((k) => k + 1)} disabled={loading}>Refresh</button>
                    <button type="button" className="tpr-btn tpr-btn--solid" onClick={exportXlsx} disabled={!data || !sorted.length}>Export to Excel</button>
                </div>
            </header>

            {loading && <div className="tpr-loadbar" role="progressbar" aria-label="Loading report" />}

            {error && (
                <div className="tpr-alert" role="alert">
                    <div>
                        <strong>The report could not be loaded.</strong>
                        <span>{error}. Check that the server is reachable and the report endpoint in the file config is correct.</span>
                    </div>
                    <button type="button" className="tpr-btn tpr-btn--ghost" onClick={() => setReloadKey((k) => k + 1)}>Try again</button>
                </div>
            )}

            {period === "custom" && (!dateFrom || !dateTo) && !data && (
                <p className="tpr-placeholder">Pick a start and end date to build the report.</p>
            )}

            {!data && loading && (
                <div className="tpr-skeleton" aria-hidden="true">
                    <div className="tpr-skel-row">{Array.from({ length: 8 }).map((_, i) => <span key={i} className="tpr-skel-card" />)}</div>
                    <span className="tpr-skel-block" />
                </div>
            )}

            {data && (
                <div className={`tpr-body${loading ? " tpr-body--stale" : ""}`}>
                    {/* period + insights */}
                    <section className="tpr-period">
                        <div className="tpr-period-main">
                            <Pill tone="blue">{pretty(P.type)}</Pill>
                            <span className="tpr-period-range">{fmtDate(P.date_from)} – {fmtDate(P.date_to)}</span>
                            <span className="tpr-period-basis">Counted by: {P.basis || "—"}</span>
                            {Object.keys(data.filters_applied || {}).length > 0 && (
                                <span className="tpr-period-basis">Filters: {Object.entries(data.filters_applied).map(([k, v]) => `${pretty(k)}=${v}`).join(", ")}</span>
                            )}
                        </div>
                        <ul className="tpr-insights">
                            {insights.map((s, i) => <li key={i}>{s}</li>)}
                        </ul>
                    </section>

                    {/* KPI row */}
                    <section aria-label="Summary">
                        <div className="tpr-kpis">
                            <KpiCard tone="blue" label="New tenders added" value={S.new_tenders_added} onClick={() => jump("status", "all")} active={tab === "all" && view === "status"} />
                            <KpiCard tone="indigo" label="Moved to Final" value={S.moved_to_final} note={pctTxt(funnel.find((s) => s.stage === "finalized")?.pct_of_new) + " of new"} onClick={() => jump("stage", "reached_finalized")} active={tab === "reached_finalized"} />
                            <KpiCard tone="cyan" label="Moved to Bid Prep" value={S.moved_to_bid_prep} note={pctTxt(funnel.find((s) => s.stage === "bid_prep")?.pct_of_new) + " of new"} onClick={() => jump("stage", "reached_bid_prep")} active={tab === "reached_bid_prep"} />
                            <KpiCard tone="cyan" label="Lying in Bid Prep" value={S.currently_lying_in_bid_prep} note="active right now" onClick={() => jump("status", "in_bid_prep")} active={tab === "in_bid_prep"} />
                            <KpiCard tone="green" label="Submitted" value={S.submitted} onClick={() => jump("status", "submitted")} active={tab === "submitted"} />
                            <KpiCard tone="red" label="Expired" value={S.expired} note="deadline passed" onClick={() => jump("status", "expired")} active={tab === "expired"} />
                            <KpiCard tone="amber" label="Closed by us" value={S.closed_by_us} onClick={() => jump("status", "closed_by_us")} active={tab === "closed_by_us"} />
                            <KpiCard tone="grey" label="Cancelled by client" value={S.cancelled_by_client} onClick={() => jump("status", "cancelled_by_client")} active={tab === "cancelled_by_client"} />
                        </div>
                    </section>

                    {/* funnel + current status */}
                    <div className="tpr-grid tpr-grid--2">
                        <section className="tpr-panel">
                            <SectionHead title="Pipeline funnel" hint="How far this period's new tenders travelled. Click a stage to list them." />
                            <Funnel stages={funnel} onPick={(stage) => { const k = STAGE_LIST_KEY[stage]; k === "all" ? jump("status", "all") : jump("stage", k); }} activeKey={activeStageKey} />
                        </section>
                        <section className="tpr-panel">
                            <SectionHead title="Where they are now" hint="Current status of the same tenders. Click a row to list them." />
                            <StatusBreakdown current={data.current_status} onPick={(k) => jump("status", k)} activeKey={view === "status" ? tab : null} />
                        </section>
                    </div>

                    {/* closures */}
                    <section>
                        <SectionHead title="Tenders that ended without a bid" hint="Expired, closed by us, or cancelled by the client." />
                        <div className="tpr-grid tpr-grid--3">
                            <ClosureCard title="Expired" tone="red" block={data.expired} onOpen={() => jump("status", "expired")} />
                            <ClosureCard title="Closed by us" tone="amber" block={data.closed_by_us} onOpen={() => jump("status", "closed_by_us")} />
                            <ClosureCard
                                title="Cancelled by client" tone="grey" block={data.cancelled_by_client} onOpen={() => jump("status", "cancelled_by_client")}
                                extra={<p className="tpr-proof">Proof uploaded for <strong>{nf(data.cancelled_by_client?.with_proof_uploaded ?? 0)}</strong> of {nf(data.cancelled_by_client?.total ?? 0)}</p>}
                            />
                        </div>
                    </section>

                    {/* breakdown + deadlines */}
                    <div className="tpr-grid tpr-grid--2">
                        <section className="tpr-panel">
                            <SectionHead title="Breakdown" hint={`Based on the ${nf(afterDropdowns.length)} tenders in the table below. Click a bar to filter.`} />
                            <Breakdown list={baseList} filters={filters} onPick={setFilter} />
                        </section>
                        <section className="tpr-panel">
                            <SectionHead title="Next deadlines" hint="Active tenders from this period, soonest first." />
                            <Deadlines list={allTenders} onOpen={openFromDeadline} />
                        </section>
                    </div>

                    {/* table */}
                    <section className="tpr-panel tpr-panel--table" ref={tableRef}>
                        <SectionHead
                            title="Tender list"
                            hint={`${nf(sorted.length)} of ${nf(baseList.length)} tenders shown`}
                            right={activeFilterCount > 0 && <button type="button" className="tpr-link-btn" onClick={clearAll}>Clear {activeFilterCount} filter{activeFilterCount > 1 ? "s" : ""}</button>}
                        />

                        <div className="tpr-viewbar">
                            <div className="tpr-seg tpr-seg--small" role="tablist" aria-label="Group tenders by">
                                <button type="button" role="tab" aria-selected={view === "status"} className={`tpr-seg-btn${view === "status" ? " tpr-seg-btn--on" : ""}`} onClick={() => jump("status", "all")}>By current status</button>
                                <button type="button" role="tab" aria-selected={view === "stage"} className={`tpr-seg-btn${view === "stage" ? " tpr-seg-btn--on" : ""}`} onClick={() => jump("stage", "reached_finalized")}>By stage reached</button>
                            </div>
                        </div>

                        <div className="tpr-tabs" role="tablist">
                            <button type="button" role="tab" aria-selected={tab === "all"} className={`tpr-tab${tab === "all" ? " tpr-tab--on" : ""}`} onClick={() => { setTab("all"); setPage(1); setOpen(null); }}>
                                All <span>{allTenders.length}</span>
                            </button>
                            {(view === "status"
                                ? STATUS_ORDER.map((k) => ({ key: k, label: data.current_status?.[k]?.label || STATUS_FALLBACK_LABEL[k], n: (T.by_status?.[k] || []).length }))
                                : STAGE_TABS.map((s) => ({ ...s, n: (T.by_stage?.[s.key] || []).length }))
                            ).map((x) => (
                                <button key={x.key} type="button" role="tab" aria-selected={tab === x.key}
                                    className={`tpr-tab${tab === x.key ? " tpr-tab--on" : ""}${x.n === 0 ? " tpr-tab--zero" : ""}`}
                                    onClick={() => { setTab(x.key); setPage(1); setOpen(null); }}>
                                    {x.label} <span>{x.n}</span>
                                </button>
                            ))}
                        </div>

                        <div className="tpr-filters">
                            <input type="search" className="tpr-input tpr-input--search" placeholder="Search ID, code, name, client, state…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Search tenders" />
                            {[["sector", "All sectors"], ["client", "All clients"], ["state", "All states"], ["tender_type", "All types"]].map(([k, ph]) => (
                                <select key={k} className="tpr-input" value={filters[k]} onChange={(e) => setFilter(k, e.target.value)} aria-label={ph}>
                                    <option value="">{ph}</option>
                                    {options[k].map((o) => <option key={o} value={o}>{pretty(o)}</option>)}
                                </select>
                            ))}
                            {filters.lead_partner && (
                                <button type="button" className="tpr-chip tpr-chip--on" onClick={() => setFilter("lead_partner", "")}>Lead: {filters.lead_partner} ×</button>
                            )}
                        </div>

                        <div className="tpr-table-wrap">
                            <table className="tpr-table">
                                <thead>
                                    <tr>
                                        <th className="tpr-th-narrow" aria-label="Expand" />
                                        <th className="tpr-th-sort" onClick={() => toggleSort("code")}>Project code{sortMark("code")}</th>
                                        <th className="tpr-th-sort" onClick={() => toggleSort("tender_id")}>Tender ID{sortMark("tender_id")}</th>
                                        <th className="tpr-th-name">Tender name</th>
                                        <th className="tpr-th-sort" onClick={() => toggleSort("client")}>Client{sortMark("client")}</th>
                                        <th className="tpr-th-sort" onClick={() => toggleSort("sector")}>Sector{sortMark("sector")}</th>
                                        <th className="tpr-th-sort" onClick={() => toggleSort("state")}>State{sortMark("state")}</th>
                                        <th>Type</th>
                                        <th className="tpr-th-sort" onClick={() => toggleSort("created_at")}>Added{sortMark("created_at")}</th>
                                        <th className="tpr-th-sort" onClick={() => toggleSort("submission_date")}>Submission{sortMark("submission_date")}</th>
                                        <th>Lead partner</th>
                                        <th className="tpr-th-sort" onClick={() => toggleSort("stage")}>Stage reached{sortMark("stage")}</th>
                                        <th className="tpr-th-sort" onClick={() => toggleSort("status")}>Status{sortMark("status")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((t) => {
                                        const d = daysLeft(t.submission_date);
                                        const isOpen = open === t.id;
                                        return (
                                            <FragmentRow key={t.id}
                                                row={(
                                                    <tr className={`tpr-row${isOpen ? " tpr-row--open" : ""}`} onClick={() => setOpen(isOpen ? null : t.id)}>
                                                        <td className="tpr-th-narrow">
                                                            <button type="button" className="tpr-expander" aria-expanded={isOpen} aria-label={isOpen ? "Hide details" : "Show details"}
                                                                onClick={(e) => { e.stopPropagation(); setOpen(isOpen ? null : t.id); }}>{isOpen ? "–" : "+"}</button>
                                                        </td>
                                                        <td className="tpr-mono">{t.company_project_code_bd}</td>
                                                        <td className="tpr-mono tpr-mono--id">{t.tender_id}</td>
                                                        <td className="tpr-name" title={t.tender_name}><span>{t.tender_name}</span></td>
                                                        <td>
                                                            <button type="button" className="tpr-cell-link" onClick={(e) => { e.stopPropagation(); setFilter("client", filters.client === t.client ? "" : t.client); }}>{pretty(t.client)}</button>
                                                        </td>
                                                        <td><Pill tone={t.sector === "Railway" ? "cyan" : "blue"}>{t.sector}</Pill></td>
                                                        <td>{t.state}</td>
                                                        <td>{pretty(t.tender_type)}</td>
                                                        <td className="tpr-nowrap">{fmtDate(t.created_at)}</td>
                                                        <td className="tpr-nowrap">
                                                            {fmtDate(t.submission_date)}
                                                            <Pill tone={dayTone(d)}>{dayText(d)}</Pill>
                                                            {t.corrigendum_date && <Pill tone="purple" title={`Corrigendum ${fmtDate(t.corrigendum_date)}`}>Corrigendum</Pill>}
                                                        </td>
                                                        <td>{t.lead_partner ? (
                                                            <button type="button" className="tpr-cell-link" onClick={(e) => { e.stopPropagation(); setFilter("lead_partner", filters.lead_partner === t.lead_partner ? "" : t.lead_partner); }}>{t.lead_partner}</button>
                                                        ) : "—"}</td>
                                                        <td className="tpr-nowrap">{t.stage_reached_label || pretty(t.stage_reached)}</td>
                                                        <td><Pill tone={STATUS_TONE[t.current_status] || "grey"}>{t.status_label || pretty(t.current_status)}</Pill></td>
                                                    </tr>
                                                )}
                                                detail={isOpen && (<tr className="tpr-detail-row"><td colSpan={13}><DetailPanel t={t} /></td></tr>)}
                                            />
                                        );
                                    })}
                                </tbody>
                            </table>
                            {rows.length === 0 && (
                                <div className="tpr-empty tpr-empty--table">
                                    <strong>No tenders match.</strong>
                                    <span>{activeFilterCount ? "Remove a filter or change the search to see more." : "This view has no tenders in the selected period."}</span>
                                    {activeFilterCount > 0 && <button type="button" className="tpr-btn tpr-btn--ghost" onClick={clearAll}>Clear filters</button>}
                                </div>
                            )}
                        </div>

                        <footer className="tpr-pager">
                            <label className="tpr-pager-size">Rows
                                <select className="tpr-input" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                                    {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                                </select>
                            </label>
                            <span className="tpr-pager-info">Page {safePage} of {pages}</span>
                            <div className="tpr-pager-btns">
                                <button type="button" className="tpr-btn tpr-btn--ghost" disabled={safePage === 1} onClick={() => setPage(1)}>First</button>
                                <button type="button" className="tpr-btn tpr-btn--ghost" disabled={safePage === 1} onClick={() => setPage(safePage - 1)}>Previous</button>
                                <button type="button" className="tpr-btn tpr-btn--ghost" disabled={safePage === pages} onClick={() => setPage(safePage + 1)}>Next</button>
                                <button type="button" className="tpr-btn tpr-btn--ghost" disabled={safePage === pages} onClick={() => setPage(pages)}>Last</button>
                            </div>
                        </footer>
                    </section>
                </div>
            )}
        </div>
    );
}

/* renders a data row plus its optional detail row without wrapper markup */
function FragmentRow({ row, detail }) {
    return (<>{row}{detail}</>);
}